import { Component, inject } from '@angular/core';
import { LabelComponent } from '../../form/label/lable';
import { CheckboxComponent } from '../../form/input/checkbox';
import { ButtonComponent } from '../../ui/button/button';
import { InputFieldComponent } from '../../form/input/input-field';
import { Router, RouterModule } from '@angular/router';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../../shared/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { AUTH_NOTICE_KEY, AuthStateService } from '../../../shared/states/auth.state';
import { NotificationService } from '../../../shared/services/notification.service';
import { passwordComplexityValidator, passwordComplexityMessage } from '../../../shared/validators/password.validator';
import { properEmailValidator } from '../../../shared/validators/email.validator';
import { SocialLoginButtonsComponent } from '../social-login-buttons/social-login-buttons';
import { ErrorToast } from '../../ui/error-toast/error-toast';

@Component({
  selector: 'app-login-form',
  imports: [
    LabelComponent,
    CheckboxComponent,
    ButtonComponent,
    InputFieldComponent,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    SocialLoginButtonsComponent,
    ErrorToast
  ],
  templateUrl: './login-form.html',
})
export class SigninFormComponent {

  private fb = inject(FormBuilder);

  showPassword = false;
  isChecked = false;
  isLoading = false;

  showSignInError = false;
  signInErrorMessage = 'Invalid email or password. Please try again.';

  /** Set when login is rejected because the email isn't verified yet. */
  unverifiedEmail: string | null = null;
  isResendingVerification = false;



  constructor(private authService: AuthService, private toastr: ToastrService, private router: Router, private authState: AuthStateService, private notificationService: NotificationService) {
    this.showSessionEndedNotice();
  }

  /** Explains why the user landed here when their session was ended remotely
   *  (password changed on another device, signed out elsewhere, …). */
  private showSessionEndedNotice(): void {
    if (typeof window === 'undefined') return;
    try {
      const notice = sessionStorage.getItem(AUTH_NOTICE_KEY);
      if (!notice) return;
      sessionStorage.removeItem(AUTH_NOTICE_KEY);
      setTimeout(() => this.toastr.info(notice, 'Signed out'));
    } catch { /* storage unavailable */ }
  }

  form = this.fb.group({
    email: ['', [Validators.required, properEmailValidator]],
    password: ['', [
      Validators.required,
      Validators.minLength(8),
      passwordComplexityValidator
    ]],
  });

  get f() {
    return this.form.controls;
  }

  fieldError(name: keyof typeof this.form.controls): string {
    const ctrl = this.f[name] as AbstractControl;

    if (!ctrl.invalid || !(ctrl.touched || ctrl.dirty)) return '';

    const e = ctrl.errors!;

    if (e['required'] || e['requiredTrue']) return this.requiredMsg(name);
    if (e['minlength'])
      return `Minimum ${e['minlength'].requiredLength} characters required`;
    if (e['email']) return 'Invalid email format';
    if (e['needsUppercase'] || e['needsNumber'] || e['hasSpace']) return passwordComplexityMessage(e);

    return 'Invalid field';
  }

  private requiredMsg(name: string): string {
    const map: Record<string, string> = {
      email: 'Email is required',
      password: 'Password is required',
    };
    return map[name] ?? 'This field is required';
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  onSignIn() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsTouched();
      ctrl.markAsDirty();
    });

    if (this.form.invalid) return;

    this.isLoading = true;
    this.unverifiedEmail = null;

    const { email, password } = this.form.getRawValue();
    const clientId = this.authState.getClientId();

    this.authService.signinUser({
      email: email!.trim(),
      password: password!,
      clientId: clientId ?? undefined,
    })
      .subscribe({
        next: (res: any) => {
          this.isLoading = false;
          this.authState.setAuth({
            isLoggedIn: true,
            user: res.data.user,
            token: res.data.accessToken,
            refreshToken: res.data.refreshToken,
          });
          this.notificationService.connect(res.data.accessToken);
          this.toastr.success(res.message);
          this.form.reset();
          this.router.navigate(['/all']);
        },
        error: (err: any) => {
          this.isLoading = false;
          if (err?.error?.code === 'EMAIL_NOT_VERIFIED') {
            this.unverifiedEmail = email!.trim();
            return;
          }
          this.signInErrorMessage = err?.error?.message ?? 'Invalid email or password. Please try again.';
          this.showSignInError = true;
        }
      });
  }

  resendVerification(): void {
    if (!this.unverifiedEmail || this.isResendingVerification) return;
    this.isResendingVerification = true;
    this.authService.resendVerification(this.unverifiedEmail).subscribe({
      next: () => {
        this.isResendingVerification = false;
        this.toastr.success(`A new verification link has been sent to ${this.unverifiedEmail}. It expires in 24 hours.`);
      },
      error: (err: any) => {
        this.isResendingVerification = false;
        this.toastr.error(err?.error?.message ?? 'Could not resend the verification email. Please try again.', 'Error');
      },
    });
  }

  closeSignInError(): void {
    this.showSignInError = false;
  }

  handleGoogleCredential(idToken: string) {
    const clientId = this.authState.getClientId();

    this.authService.googleLogin(idToken, clientId ?? undefined).subscribe({
      next: (res: any) => {
        this.authState.setAuth({
          isLoggedIn: true,
          user: res.data.user ?? null,
          token: res.data.accessToken,
          refreshToken: res.data.refreshToken,
        });
        this.notificationService.connect(res.data.accessToken);
        this.router.navigate(['/all']);
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.message ?? 'Google sign in failed!', 'Error');
      }
    });
  }

  handleFacebookToken(accessToken: string) {
    const clientId = this.authState.getClientId();

    this.authService.facebookLogin(accessToken, clientId ?? undefined).subscribe({
      next: (res: any) => {
        this.authState.setAuth({
          isLoggedIn: true,
          user: res.data.user ?? null,
          token: res.data.accessToken,
          refreshToken: res.data.refreshToken,
        });
        this.notificationService.connect(res.data.accessToken);
        this.router.navigate(['/all']);
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.message ?? 'Facebook sign in failed!', 'Error');
      }
    });
  }
}
